import useNode from './useNode'
import camelcaseKeys from 'camelcase-keys'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { isEmpty } from '../utilities/general'
import { Database } from '../types/supabase'
import { supabase } from '../supabaseClient'
import { create } from 'zustand'
import { callTool } from '../lib/tools'
import { servicePublishArgs, serviceUpdateArgs, type ServiceFormData } from '../utilities/routerWrites'
import { eventClock, rowsAfterRead } from '../utilities/rowsAfterRead'
import { readWhenStreamStarts } from '../utilities/streamStart'

export interface ServicesState {
  services: Service[]
  set: (state: Partial<ServicesState>) => void
  get: () => Promise<void>
  /** Reads the services of the nodes that have just entered the node store, in one query. */
  getForNodes: (nodeIds: string[]) => Promise<void>
  getByNodeId: (nodeId: string) => Service[]
  /**
   * The org of the service with this code (`services.name`): the org of its node. Two direct
   * reads: the service's node, then that node (`useNode.orgOf`). `false` when the reader can read
   * no such service, null when a read failed (app#477).
   */
  orgOfCode: (code: string) => Promise<string | false | null>
  add: (service: Service) => void
  update: (service: Partial<Service>) => void
  remove: (serviceId: string) => void
  /** Publishes a new service on `nodeId` through `service_publish`. Throws the router's refusal. */
  publish: (nodeId: string, form: ServiceFormData) => Promise<void>
  /** Changes a service through `service_update`. Throws the router's refusal. */
  edit: (service: Service, form: ServiceFormData) => Promise<void>
  /** Deletes a service through `service_delete`. Throws the router's refusal. */
  delete: (id: string) => Promise<void>
  subscribe: () => () => void
  reset: () => void
}

const defaultService: Service = {
  id: '',
  createdAt: '',
  enabled: true,
  name: '',
  nodeId: '',
  title: null,
  url: null,
  command: null,
  admin: false,
  workdir: null,
  authRequired: false,
}

const state = {
  services: [],
}

// ⚠⚠ A READ MUST NOT OVERWRITE A NEWER EVENT (app#437). This store has two writers: the reads
// (`get`, `getForNodes`) and the realtime handler. The answer of a read is as old as the moment the
// database made it, and an event can arrive between that moment and the answer. The handler marks
// each event it applies on this clock, by service id. A read asks the clock when it is SENT, and
// its answer does not touch a service that an event changed after that (`rowsAfterRead`).
const events = eventClock()

// ⚠ Each full read takes a number, and only the LATEST one may write, as in `useNode.get()`. Two
// full reads can overlap (a join, an org switch, the offline filter), and the answers do not
// arrive in order: without this, an older answer that lands last removes what the newer one repaired.
let latestGet = 0

const useServices = create<ServicesState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const seq = ++latestGet
    const loadedNodeIds = useNode.getState().nodes.map(node => node.id)
    if (!loadedNodeIds.length) return set({ services: [] })
    const asked = new Set(loadedNodeIds)
    const newer = events.readStarts()

    // Tombstones are the caller's job to exclude, exactly as in useNode/useNetwork:
    // the owner's RLS policy stopped filtering them so that a deletion (a soft
    // delete, so an UPDATE) can reach the realtime handler below at all.
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .is('deleted_at', null)
      .in('node_id', loadedNodeIds)
      .order('created_at', { ascending: false })

    if (seq !== latestGet) return
    if (error) console.warn(error)
    // ⚠ The read asked for `loadedNodeIds` only. A node that entered the store after that has its
    // own read (`getForNodes`), and this answer must not remove what that read brought.
    else applyRead(data.map(adapt).filter(Boolean) as Service[], newer, s => asked.has(s.nodeId))
  },
  // ⚠⚠ `get()` READS FOR THE NODE IDS OF ONE MOMENT, and realtime carries changes, never the
  // rows that exist. So a node that enters the node store later (it was hidden by `hideOffline`
  // and connects again, it registers, or a channel rejoin finds it) had NO services here until a
  // reload: its page showed none, and the user published them again (app#436). `useNode` calls
  // this for the nodes that enter the store by a path that is not a full read.
  //
  // ⚠ ONE query for all the ids. A rejoin can find many new nodes at once, and one read for each
  // was 50 reads for 50 nodes.
  // ⚠ A service published during the read arrives by its own event first, and the answer is
  // older and does not have it. `applyRead` keeps the event.
  // ⚠ The answer can arrive after a node left the store (an org switch, the offline filter).
  // Its rows are dropped then, the same as the realtime handler below drops them.
  getForNodes: async nodeIds => {
    if (!nodeIds.length) return
    const asked = new Set(nodeIds)
    const newer = events.readStarts()
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .is('deleted_at', null)
      .in('node_id', nodeIds)
      .order('created_at', { ascending: false })

    if (error) return console.warn(error)
    applyRead(data.map(adapt).filter(Boolean) as Service[], newer, s => asked.has(s.nodeId))
  },
  orgOfCode: async code => {
    // ⚠ `eq`, not `ilike`: `services.name` has a collation that compares with no case, and
    // PostgreSQL refuses a pattern match on such a column.
    const { data, error } = await supabase
      .from('services')
      .select('node_id')
      .eq('name', code)
      .is('deleted_at', null)
      .limit(1)
    if (error) {
      console.warn(error)
      return null
    }
    const nodeId = (data ?? [])[0]?.node_id
    if (!nodeId) return false
    return useNode.getState().orgOf(nodeId)
  },
  getByNodeId: (nodeId: string) => {
    return get().services.filter(service => service.nodeId === nodeId)
  },
  add: service => {
    const services = [...get().services]
    services.push(service)
    set({ services })
  },
  update: service => {
    const { id, ...data } = service
    const services = [...get().services]

    const index = services.findIndex(s => s.id === id)
    if (index === -1) {
      // A tombstone for a service we are not holding is nothing to ADD. get() excludes
      // deleted rows, so pushing one here would put a deleted service on screen — the
      // exact inverse of the bug the splice below fixes, and reachable the moment 0101
      // starts delivering these events (a service whose row was already deleted before
      // this session loaded, then touched again).
      if (data.deletedAt) return
      services.push({ ...defaultService, ...service })
    } else if (isEmpty(data) || data.deletedAt) {
      // A deleted service arrives here as an UPDATE, never as a DELETE: the
      // services_soft_delete trigger rewrites the delete into deleted_at = now().
      // Without this the row stayed on screen until the next full page load.
      services.splice(index, 1)
    } else {
      services[index] = { ...services[index], ...service }
    }

    set({ services })
  },
  remove: serviceId => {
    let services = [...get().services]
    services = services.filter(s => s.id !== serviceId)
    set({ services })
  },
  // ⚠⚠ THROUGH THE ROUTER, NEVER POSTGREST (app#373, docs/PERMISSIONS.md § 9). A service belongs
  // to its node, and only the node's controllers (its owner, or an org Owner/Admin) may publish,
  // change or delete one. PostgREST let any Member upsert any column of any service in the org; the
  // router asks the rule at each call. The row reaches this store through realtime, as before.
  //
  // ⚠ These THROW instead of alerting, so the form stays open and says why (AddService,
  // EditService). The form closed after a failed save before this.
  publish: async (nodeId, form) => {
    await callTool('service_publish', servicePublishArgs(nodeId, form))
  },
  edit: async (service, form) => {
    const args = serviceUpdateArgs(service, form)
    if (args) await callTool('service_update', args)
  },
  delete: async id => {
    await callTool('service_delete', { service_id: id })
  },
  subscribe: () => {
    const { update, remove } = get()
    // ⚠⚠ READ AGAIN AFTER EVERY (RE)JOIN (app#437), for the reason `useNode.subscribe` gives
    // (app#366): an event that is sent while the channel is down is gone, and nothing else
    // reads this store again short of a reload, an org switch or a wake. A service that was
    // published in the gap did not show, and the user published it again.
    // ⚠⚠ THE READ IS MADE WHEN THE CHANGE STREAM STARTS, NOT ON `SUBSCRIBED` (app#439,
    // `readWhenStreamStarts`): the server starts the stream after it answers the join.
    // ⚠ This read is for the nodes the store holds NOW. The nodes channel rejoins at the same
    // time and its re-read can find a new node later: `useNode` reads that node's services
    // itself (`loadNodeDerived`), and `applyRead` keeps them when this answer arrives last.
    const stream = readWhenStreamStarts(() => void get().get())

    const subscription = supabase
      .channel('services-all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'services' },
        (payload: RealtimePostgresChangesPayload<Database['public']['Tables']['services']['Row']>) => {
          console.log('Service change received!', payload.eventType, payload)
          const service = adapt(payload.new)
          // Stay scoped to the loaded nodes, the way useNode/useNetwork scope to the active
          // org: get() only ever holds services of nodes this store can see, so a service
          // hanging off a node that isn't loaded — another org you belong to, or one hidden
          // by the default hideOffline — must not leak in through realtime. (DELETE is safe
          // to apply unconditionally; it can only remove, and payload.new is empty there.)
          if (payload.eventType !== 'DELETE' && service && !useNode.getState().nodes.some(n => n.id === service.nodeId)) {
            return
          }
          // Mark this event, for a read that is in flight (see `events`).
          const id = payload.eventType === 'DELETE' ? payload.old.id : service?.id
          if (id) events.mark(id)
          switch (payload.eventType) {
            // ⚠ An INSERT goes through `update`, which adds a service the store does not hold and
            // merges one it holds. A read on a join or for a new node can put the row in the store
            // BEFORE its INSERT event arrives, and `add` then held the service twice.
            case 'INSERT':
            case 'UPDATE':
              if (service) update(service)
              break
            case 'DELETE':
              if (payload.old.id) remove(payload.old.id)
              break
          }
        }
      )
      .on('system', {}, stream.onSystem)
      .subscribe(stream.onStatus)

    return () => {
      console.log('Unsubscribing from service changes')
      stream.stop()
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

/**
 * Put the answer of a read in the store. `covers` says which services the read asked for.
 *
 * ⚠ A service that an event changed after the read was sent (`newer`) keeps what the event wrote.
 * ⚠ A service of a node that the node store no longer holds does not stay: the node left during
 * the read (an org switch, the offline filter), and its rows must not show under the new set.
 */
function applyRead(read: Service[], newer: (id: string) => boolean, covers: (service: Service) => boolean) {
  const loaded = new Set(useNode.getState().nodes.map(node => node.id))
  const services = rowsAfterRead(useServices.getState().services, read, covers, newer)
  useServices.setState({ services: services.filter(service => loaded.has(service.nodeId)) })
}

function adapt(raw: Record<string, unknown>): Service | null {
  const service = camelcaseKeys(raw) as Service
  if (service.mcp) return null
  if (service.name) service.name = service.name.toLowerCase()
  return service
}

export default useServices
